import React, { FC, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store/store";
import { Form, message, Space, Tag } from "antd";
import ClaimIDManuallyId from "../../../components/patient/PreAuth/ClaimIDManuallyId";
import ManualStatus from "../../../components/patient/PreAuth/ManualStatusButton";
import ManualPortalStatus from "../../../components/patient/PreAuth/ManualPortalStatusButton";
import { PreAuthClaimAyushB } from "../../../components/patient/PreAuthClaim/ClaimAyushB/PreAuthClaimAyushB";
import { PreAuthAyush } from "../../../components/patient/PreAuth/PreAuthAyush/PreAuthAyushB";
import {
  getDoctors,
  getPortalDataForAyushB,
  getProcedures,
  getStratifications,
  resetAyushState,
} from "../../../models/ayushmanBSlice";
import {
  deriveOptionsFromTreatmentPlans,
  mapImplantsToDocTypes,
  mapInvestigationsToDocTypes,
} from "../../../utils/ayushmanTreatmentPlanMapper";
import { IUploadReferral } from "../../../interfaces/IReferral";
import FilePreviewModal from "../../../components/patient/PreAuth-query/FilePreviewModal";
import RightSidePreviewModal from "../../../components/modals/RightSidePreviewModal";
import { UploadDocument } from "../../../models/visitSlice";
import { errorMessage, successMessage } from "../../../utils/notificationUtils";
import {
  deleteUploadedDocument,
  downloadDocument,
  enhancementService,
  getAllStarhealthDocumentsData,
} from "../../../apis/patientService";
import { IDocumentFields } from "../../../interfaces/ICase";
import DocumentDeficiencyModal from "../../../components/patient/DocumentDeficiencyModal";
import {
  clearDeficiencyCheck,
  getDeficiencyQuestionData,
  getSchemeMetaData,
} from "../../../models/patientSlice";
import { Button } from "antd";
import {
  CloudDownloadOutlined,
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { getVisitStageStates } from "../../../models/preAuthSlice";
import UploadDocumentInPreAuth from "../../../components/patient/PreAuth/UploadDocumentInPreAuth";
import UploadDocumentInAyushBPreAuth from "../../../components/patient/PreAuth/PreAuthAyush/UploadDocumentInAyushBPreAuth";
import UploadDocumentInPreAuthImplant from "../../../components/patient/PreAuth/PreAuthAyush/UploadDocumentInAyushBPreAuth";

const styles: Record<string, React.CSSProperties> = {
  mainContent: {
    border: "0.4px solid rgb(87, 87, 87)",
    gap: "15px",
    borderRadius: "16px",
    margin: "0px 0.5rem",
  },
  statusDesign: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "20px",
    minWidth: "676px",
    maxHeight: "36px",
  },
  borderDesign: {
    minHeight: "64px",
    background: "rgb(243, 243, 243)",
    padding: "12px 35px",
    gap: "42px",
    borderRadius: "16px 16px 0px 0px",
    borderBottom: "1px solid rgb(166, 166, 166)",
    fontWeight: "600",
    fontSize: "16px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  filledBtn: {
    background: "#008B8B",
    border: "1px solid #008B8B",
    boxShadow: "0px 2px 8px 0px #00000040",
    borderRadius: "10px",
    padding: "12px 25px",
    textTransform: "uppercase",
    color: "white",
    marginRight: "0.8rem",
  },
  blankBtn: {
    color: "#008B8B",
    background: "white",
    border: "1px solid #008B8B",
    boxShadow: "0px 2px 8px 0px #00000040",
    borderRadius: "10px",
    padding: "12px 25px",
    marginRight: "0.8rem",
  },
  updateBtn: {
    position: "sticky",
    top: 0,
    backgroundColor: "white",
    zIndex: 998,
    padding: "10px 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "end",
  },
  intimateSubmitBtn: {
    background: "#008B8B",
    marginRight: "0.8rem",
  },
  saveBtn: {
    color: "white",
    background: "#008B8B",
    border: "1px solid #008B8B",
    boxShadow: "0px 2px 8px 0px #00000040",
    borderRadius: "10px",
    padding: "12px 16px",
  },
  editBtn: {
    color: "#008B8B",
    background: "white",
    border: "1px solid #008B8B",
    boxShadow: "0px 2px 8px 0px #00000040",
    borderRadius: "10px",
    padding: "12px 16px",
  },
};

const STATUS = {
  INACTIVE: 4,
  COMPLETE: 8,
};

interface UploadedFileType {
  key: string;
  id: number;
  documentType: string;
  documentName: string;
  deficiencyCheckStatus: boolean | null;
  documentUrl: string;
}

interface Document {
  id: number;
  type?: string;
  file?: any;
  documentId?: any;
  label?: string;
  name?: string;
  labelId?: string | number;
  documentUniqueCode?: string;
  documentName?: string;
}

interface UploadedDocList {
  implant: IDocumentFields[];
  investigation: IDocumentFields[];
}

interface AyushBContainerProp {
  nextStage: () => void;
  stepperForStatus: CallableFunction;
  setMoveToNext: (move: boolean) => void;
}

const PreAuthAyushConatiner: React.FC<AyushBContainerProp> = ({
  nextStage,
  setMoveToNext,
  stepperForStatus,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [form] = Form.useForm();
  const { patients, preAuth, preAuthClaim, enhancement, auth, ayushBPreAuth } =
    useSelector((state: RootState) => state);

  const currentActiveStage =
    preAuth?.stepperData?.visitStages?.[preAuth?.currentStep];
  const currentStageDetails = preAuth?.stepperData?.currentVisitStage;

  const patientStore = patients?.fetchBasicDetails;
  const caseId = patientStore?.casesResponse?.id;
  const visitId = patientStore?.visitInfoResponse?.id;
  const schemeMetaDataId = patientStore?.casesResponse?.schemeMetaDataId;
  const schemeMetaData = patients?.schemeMetaData;

  const [manualStatusUpdateCount, setManualStatusUpdateCount] =
    useState<number>(0);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [uploadFileModal, setUploadFileModal] = useState<boolean>(false);
  const [previewDocument, setPreviewDocument] = useState<boolean>(false);
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const [selectedDocumentType, setSelectedDocumentType] = useState<
    string | null
  >(null);
  const [documentTypeList, setDocumentTypeList] = useState<{
    [key: string]: Document[];
  }>({});
  const [formattedDocumentTypeList, setFormattedDocumentTypeList] = useState<{
    [key: string]: Document[];
  }>({});
  const [uploadedInvestigationFileList, setUploadedInvestigationFileList] =
    useState<IDocumentFields[]>([]);
  const [uploadedImplantFileList, setUploadedImplantFileList] = useState<
    IDocumentFields[]
  >([]);
  const [previewDocumentUrl, setPreviewDocumentUrl] = useState<string>("");
  const [documentName, setDocumentName] = useState<string>("");
  const [stageStatus, setStageStatus] = useState<number | null>(null);
  const [deficiencyModal, setDeficiencyModal] = useState<boolean>(false);
  const [deficiencyCheckQuestions, setDeficiencyCheckQuestions] =
    useState<any>(null);
  const [selectedDocument, setSelectedDocument] =
    useState<IDocumentFields | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [activeDocListType, setActiveDocListType] = useState<string>("");
  const [claimId, setClaimId] = useState("");
  const [selectedFile, setSelectedFile] = useState<IUploadReferral>({
    file: {},
    documentType: "",
  });

  const [previewFile, setPreviewFile] = useState<{
    url: string;
    name: string;
    showUpload: boolean;
    documentType: string;
  }>({
    url: "",
    name: "",
    showUpload: false,
    documentType: "",
  });

  // Keep all investigation/implant types available so multiple files
  // can be uploaded for the same type. Delete removes a single file only.
  useEffect(() => {
    if (documentTypeList?.implant) {
      setFormattedDocumentTypeList((prev) => ({
        ...prev,
        ["implant"]: documentTypeList?.implant || [],
      }));
    }
  }, [documentTypeList?.implant]);

  useEffect(() => {
    if (documentTypeList?.investigation) {
      setFormattedDocumentTypeList((prev) => ({
        ...prev,
        ["investigation"]: documentTypeList?.investigation || [],
      }));
    }
  }, [documentTypeList?.investigation]);

  useEffect(() => {
    if (currentActiveStage?.visitStageId) {
      dispatch(
        getVisitStageStates({ visitStageId: currentActiveStage?.visitStageId }),
      );
    }
  }, [currentActiveStage?.visitStageId]);

  useEffect(() => {
    handleGetData();
  }, [caseId, visitId]);

  useEffect(() => {
    handleDropdownData();
  }, [schemeMetaData]);

  useEffect(() => {
    if (ayushBPreAuth?.portalData?.casePreAuthOthersResponse) {
      if (
        ayushBPreAuth?.portalData?.casePreAuthOthersResponse?.investigationList
      )
        getAttachmentsWithValues(
          ayushBPreAuth?.portalData?.casePreAuthOthersResponse
            ?.investigationList,
          "investigation",
        );
      if (ayushBPreAuth?.portalData?.casePreAuthOthersResponse?.implantList)
        getAttachmentsWithValues(
          ayushBPreAuth?.portalData?.casePreAuthOthersResponse?.implantList,
          "implant",
        );

      setClaimId(
        ayushBPreAuth?.portalData?.casePreAuthOthersResponse?.claimId || "",
      );
    }
  }, [ayushBPreAuth?.portalData?.casePreAuthOthersResponse]);

  useEffect(() => {
    if (!currentActiveStage?.step || !currentStageDetails?.step) {
      setStageStatus(null);
      return;
    }

    if (currentActiveStage.step === currentStageDetails.step) {
      setStageStatus(currentStageDetails.stateId);
    } else if (currentActiveStage.step > currentStageDetails.step) {
      setStageStatus(STATUS.COMPLETE);
    } else {
      setStageStatus(STATUS.INACTIVE);
    }
  }, [currentActiveStage, currentStageDetails]);

  const fetchDocuments = async (
    schemeMetaDataId: number,
    caseId: number,
    visitId: number,
  ) => {
    if (schemeMetaDataId && caseId && visitId) {
      try {
        const AllDocumentList = await getAllStarhealthDocumentsData(
          caseId,
          visitId,
          schemeMetaDataId,
        );
        setDocumentTypeList({
          ["implant"]: AllDocumentList?.["PreAuth"],
          ["investigation"]: AllDocumentList?.["PreAuth"],
        });
      } catch (err) {
        errorMessage("Please, Try again after sometime");
      }
    }
  };

  // useEffect(() => {
  //   if (schemeMetaDataId && caseId && visitId) {
  //     fetchDocuments(schemeMetaDataId, caseId, visitId);
  //   }
  // }, [schemeMetaDataId, caseId, visitId]);

  useEffect(() => {
    const plans =
      ayushBPreAuth?.portalData?.casePreAuthOthersResponse?.treatmentPlansData ||
      [];
    const proceduresRaw: any = ayushBPreAuth?.preAuth?.procedures;
    const procedures: any[] = Array.isArray(proceduresRaw)
      ? proceduresRaw
      : Array.isArray(proceduresRaw?.data)
        ? proceduresRaw?.data
        : Array.isArray(proceduresRaw?.content)
          ? proceduresRaw?.content
          : [];

    if (!plans?.length || !procedures?.length) {
      setDocumentTypeList((prev) => ({
        ...prev,
        investigation: [],
        implant: [],
      }));
      return;
    }

    const { investigations, implants } = deriveOptionsFromTreatmentPlans(
      plans,
      procedures,
    );

    setDocumentTypeList((prev) => ({
      ...prev,
      investigation: mapInvestigationsToDocTypes(investigations || []),
      implant: mapImplantsToDocTypes(implants || []),
    }));
  }, [
    ayushBPreAuth?.portalData?.casePreAuthOthersResponse?.treatmentPlansData,
    ayushBPreAuth?.preAuth?.procedures,
  ]);

  const isEditable = stageStatus !== STATUS.INACTIVE;

  const uploadedFileColumns = [
    {
      title: "S.No.",
      dataIndex: "key",
      key: "key",
      align: "center",
      width: 80,
      render: (text: any, records: any, index: number) => (
        <span style={{ fontWeight: "bold" }}>{index + 1}</span>
      ),
    },
    {
      title: "Document Type",
      dataIndex: "documentLabelType",
      key: "documentLabelType",
      align: "center",
      render: (text: string, record: any) => {
        const allTypes = [
          ...(documentTypeList?.investigation || []),
          ...(documentTypeList?.implant || []),
        ];
        const matched = allTypes.find(
          (item: any) =>
            String(item?.labelId) === String(record?.documentType) ||
            String(item?.id) === String(record?.documentType) ||
            String(item?.documentUniqueCode) === String(record?.documentType)
        );
        const name =
          record?.documentMasterName || text || matched?.name || matched?.label || "";
        const code = matched?.documentUniqueCode || "";
        const display =
          name && code && !String(name).includes(String(code))
            ? `${name} (${code})`
            : name || code || "-";
        return (
          <div style={{ textTransform: "initial", fontSize: "15px" }}>
            {display}
          </div>
        );
      },
    },
    {
      title: "Document Name",
      dataIndex: "documentName",
      key: "document",
      align: "center",
      render: (text: string) => (
        <div style={{ textTransform: "initial", fontSize: "15px" }}>{text}</div>
      ),
    },
    {
      title: "Deficiency Status",
      dataIndex: "deficiencyCheckStatus",
      key: "deficiency",
      align: "center",
      render: (text: boolean | null, record: IDocumentFields) => {
        if (record && record?.documentName) {
          setDocumentName(record?.documentName);
        }
        return text === true ? (
          <Tag color="green">Pass</Tag>
        ) : text === false ? (
          <Tag color="red">Fail</Tag>
        ) : text === null || text === undefined ? (
          <Button
            style={{
              backgroundColor: "#E0E0E0",
              borderRadius: "10px",
              color: "#222222",
              boxShadow: "0px -1px 4px 0px #00000040 inset",
            }}
            type="primary"
            onClick={() => {
              if (record?.documentType) {
                record?.documentUrl &&
                  record?.id &&
                  record &&
                  handleDeficiencyModalOpen(
                    record?.documentUrl,
                    record?.id,
                    record,
                  );
              }
            }}
          >
            <span style={{ textTransform: "initial" }}>Check Deficiency</span>
          </Button>
        ) : null;
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (_: any, record: IDocumentFields) => (
        <Space size="middle">
          <EyeOutlined
            style={{ color: "#008B8B", fontSize: "17px", cursor: "pointer" }}
            onClick={() => {
              handleOpen();
              record?.documentUrl && handleSetDocumentUrl(record?.documentUrl);
            }}
          />
          {/* <EditOutlined style={{ color: "#008B8B", fontSize: "17px", cursor: "pointer" }} /> */}
          <CloudDownloadOutlined
            style={{ color: "#008B8B", fontSize: "17px", cursor: "pointer" }}
            onClick={() =>
              record?.documentName &&
              record?.documentUrl &&
              handleDownloadDocument(record?.documentName, record?.documentUrl)
            }
          />
          <DeleteOutlined
            style={{ color: "#008B8B", fontSize: "17px", cursor: "pointer" }}
            onClick={() =>
              record?.documentType &&
              record?.documentUrl &&
              handleDeleteDocument(record?.documentType, record?.documentUrl)
            }
          />
        </Space>
      ),
    },
  ];

  const handleNext = () => setActiveStep(activeStep + 1);
  const handlePrevious = () => setActiveStep(activeStep - 1);

  const handleGetData = async () => {
    if (caseId && visitId) {
      dispatch(getPortalDataForAyushB({ caseId, visitId }));
      stepperForStatus();
    }
  };

  const handleDropdownData = async () => {
    if (
      schemeMetaData?.portalMetaDataIdsList?.length &&
      patientStore?.casesResponse?.healthCareId
    ) {
      const payload = {
        hospitalId: patientStore?.casesResponse?.healthCareId,
        schemeId: schemeMetaDataId,
      };
      dispatch(
        getProcedures({
          payload: payload,
        }),
      );
      // dispatch(getStratifications({ portalMetaDataId: schemeMetaData?.portalMetaDataIdsList[0]}));
      // dispatch(
      //   getDoctors({
      //     portalMetaDataId: schemeMetaData?.portalMetaDataIdsList[0],
      //   })
      // );
    }
  };

  const getAttachmentsWithValues = (attchmentData: any, key: string) => {
    const attachmentsWithValues: IDocumentFields[] = [];
    if (attchmentData) {
      // const investigationAttachments = attchmentData;
      // investigationAttachments?.forEach((attachment: any) => {
      //   attachmentsWithValues.push({
      //     documentName: attachment?.documentName,
      //     documentType: attachment?.documentType,
      //     documentUrl: attachment?.documentUrl,
      //     id: attachment?.id,
      //     deficiencyCheckStatus: attachment?.deficiencyCheckStatus,
      //     documentLabelType: attachment?.documentLabelType,
      //   });
      // });
      if (key == "investigation")
        setUploadedInvestigationFileList(attchmentData);
      if (key == "implant") setUploadedImplantFileList(attchmentData);
    }
  };

  const updateOrAddDocument = (
    uploadedDocumentUrl: IDocumentFields[],
    documentRes: IDocumentFields,
  ): IDocumentFields[] => {
    const updatedDocuments = [...uploadedDocumentUrl];
    const documentIndex = updatedDocuments.findIndex(
      (doc) => doc.documentType === documentRes.documentType,
    );

    if (documentIndex !== -1) {
      updatedDocuments[documentIndex] = {
        ...updatedDocuments[documentIndex],
        documentName: documentRes?.documentName,
        documentUrl: documentRes?.documentUrl,
        deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
        id: documentRes?.id,
      };
    } else {
      updatedDocuments.push({
        documentType: documentRes.documentType,
        documentName: documentRes.documentName,
        documentUrl: documentRes.documentUrl,
        documentLabelType: documentRes?.documentLabelType,
        deficiencyCheckStatus: documentRes?.deficiencyCheckStatus,
        id: documentRes?.id,
      });
    }
    return updatedDocuments;
  };

  const handleFileChange =
    (documentType: string, docLabel: string | null = null) =>
    (e: any) => {
      const maxSize = 15 * 1024 * 1024;
      if (Array.isArray(e)) {
        return e;
      }
      if (e?.fileList[0]) {
        setSelectedFile({ file: e?.fileList[0], documentType });
        setPreviewFile({
          url: URL.createObjectURL(e?.file),
          name: e.file.name,
          showUpload: true,
          documentType: documentType,
        });
        setUploadFileModal(true);
      }
      return e?.fileList;
    };

  const handleUploadDocument = async () => {
    const CollectPostData = new FormData();
    if (patients?.fetchBasicDetails?.casesResponse?.healthCareId) {
      CollectPostData.append(
        "hospitalId",
        patients?.fetchBasicDetails?.casesResponse?.healthCareId?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.patientResponse?.id) {
      CollectPostData.append(
        "patientId",
        patients?.fetchBasicDetails?.patientResponse?.id?.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.casesResponse?.id) {
      CollectPostData.append(
        "caseId",
        patients?.fetchBasicDetails?.casesResponse?.id.toString(),
      );
    }
    if (patients?.fetchBasicDetails?.visitInfoResponse?.id) {
      CollectPostData.append(
        "visitId",
        patients?.fetchBasicDetails?.visitInfoResponse?.id?.toString(),
      );
    }
    CollectPostData.append("documentType", previewFile.documentType);

    if (selectedFile?.file) {
      CollectPostData.append("file", selectedFile?.file?.originFileObj as Blob);
    }

    try {
      const resp = await dispatch(
        UploadDocument({
          formData: CollectPostData,
          accessToken: auth.accessToken || "",
        }),
      );

      if (UploadDocument.fulfilled.match(resp)) {
        const documentList =
          activeDocListType == "investigation"
            ? uploadedInvestigationFileList
            : uploadedImplantFileList;
        // const updatedDocuments = updateOrAddDocument(
        //   documentList,
        //   resp?.payload
        // );
        const updatedDocuments = [...resp?.payload, ...documentList];
        if (resp?.payload?.length > 0) {
          message.success(
            `${resp?.payload?.[0]?.documentLabelType} uploaded successfully`,
          );
          if (activeDocListType == "investigation")
            setUploadedInvestigationFileList(updatedDocuments);
          if (activeDocListType == "implant")
            setUploadedImplantFileList(updatedDocuments);
        } else {
          message.error("Please Upload Again");
        }
        setUploadFileModal(false);
      } else if (UploadDocument.rejected.match(resp)) {
        errorMessage("FAILED TO UPLOAD DOCUMENT");
      }
    } catch (error) {
      console.error("Failed to upload document:", error);
    }
  };

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleCancel = () => setUploadFileModal(false);
  const handleCancelPreview = () => setPreviewDocument(false);

  const handleDownloadDocument = async (fileName: string, url: string) => {
    try {
      await downloadDocument(fileName, url);
      successMessage("Document Downloaded Successfully");
    } catch (err: any) {
      errorMessage("Please, Try again later");
    }
  };

  const handleDeleteDocument = async (docType: string, documentUrl: string) => {
    let deleteDocument;
    if (activeDocListType == "investigation") {
      deleteDocument = uploadedInvestigationFileList?.find(
        (doc, index) => doc?.documentUrl === documentUrl,
      );
    }
    if (activeDocListType == "implant") {
      deleteDocument = uploadedImplantFileList?.find(
        (doc, index) => doc?.documentUrl === documentUrl,
      );
    }

    const deleteForm = {
      documentUrl: deleteDocument?.documentUrl,
      status: false,
      id: deleteDocument?.id,
    };

    let newUpdatedDocumentList;
    if (activeDocListType == "investigation") {
      newUpdatedDocumentList = uploadedInvestigationFileList?.filter(
        (doc, index) => doc?.documentUrl !== documentUrl,
      );
    }
    if (activeDocListType == "implant") {
      newUpdatedDocumentList = uploadedImplantFileList?.filter(
        (doc, index) => doc?.documentUrl !== documentUrl,
      );
    }
    // dispatch(setUploadedDocumentUrl(newUpdatedDocumentList));
    if (deleteDocument?.documentUrl) {
      const res = await deleteUploadedDocument(deleteForm);
      if (res) {
        message.success("Document Deleted Successfully!");
        if (activeDocListType == "investigation")
          setUploadedInvestigationFileList(newUpdatedDocumentList || []);
        if (activeDocListType == "implant")
          setUploadedImplantFileList(newUpdatedDocumentList || []);
      }
    }
  };

  const handleSetDocumentUrl = (url: string) => setDocumentUrl(url);
  const handleOpen = () => setPreviewDocument(true);

  const fetchDeficiencyData = async (
    selectedDocument: IDocumentFields,
    selectedDocumentId: number,
  ) => {
    try {
      if (selectedDocumentId) {
        dispatch(clearDeficiencyCheck());
        if (selectedDocument?.id) {
          const secondRes = await dispatch(
            getDeficiencyQuestionData(selectedDocument?.id),
          );
        }
      }
    } catch (err) {
      errorMessage("Please, Try again after sometime");
      setSelectedDocument(null);
    }
  };

  const handleDeficiencyModalOpen = async (
    documentUrl: string,
    documentId: number,
    document: IDocumentFields,
  ) => {
    await fetchDeficiencyData(document, documentId);
    setPreviewDocumentUrl(documentUrl);
    setDeficiencyModal(true);
    setSelectedDocument(document);
  };

  const handleDeficiencyModalAfterSave = async (
    documentUrl: string,
    documentId: number,
    field: any,
  ) => {
    const document = {
      id: field?.documentId,
      documentName: field?.documentName,
      documentType: field?.labelId,
      documentUrl: field?.data,
      documentLabelType: field?.label,
      deficiencyCheckStatus: field?.deficiencyCheckStatus,
    };
    await fetchDeficiencyData(document, documentId);
    setPreviewDocumentUrl(documentUrl);
    setDeficiencyModal(true);
    setSelectedDocument(document);
  };

  const closeDeficiencyModal = () => {
    setDeficiencyModal(false);
    setSelectedDocument(null);
  };

  return (
    <div style={styles.mainContent}>
      <div style={styles.borderDesign}>
        <span style={{ fontSize: "20px", alignContent: "center" }}>
          PREAUTH
        </span>
        <div style={styles.statusDesign}>
          <ClaimIDManuallyId handlePortalData={handleGetData} />
          {currentActiveStage?.visitStageId &&
            patientStore?.visitInfoResponse?.id && (
              <ManualStatus
                stateData={
                  preAuth?.stageAllState?.[currentActiveStage?.visitStageId] ||
                  null
                }
                status={stageStatus}
                visitId={patientStore?.visitInfoResponse?.id}
                visitStageId={currentActiveStage?.visitStageId}
                isUpdateable={isEditable}
                // setManualStatusUpdateCount={setManualStatusUpdateCount}
              />
            )}
          {patientStore?.visitInfoResponse?.id && (
            <ManualPortalStatus
              // statusList={preAuth?.schemePortalStatusList || []}
              status={preAuth?.portalData?.portalStatus || "-"}
              visitId={patientStore?.visitInfoResponse?.id}
              isUpdateable={isEditable}
              // setManualStatusUpdateCount={setManualStatusUpdateCount}
            />
          )}
        </div>
      </div>
      <PreAuthAyush
        activeStep={activeStep}
        handleNext={handleNext}
        handlePrevious={handlePrevious}
        form={form}
        setActiveDocListType={setActiveDocListType}
        activeDocListType={activeDocListType}
        setSelectedDocumentType={setSelectedDocumentType}
        selectedDocumentType={selectedDocumentType}
        setUploadedInvestigationFileList={setUploadedInvestigationFileList}
        setUploadedImplantFileList={setUploadedImplantFileList}
        uploadedImplantFileList={uploadedImplantFileList}
        uploadedInvestigationFileList={uploadedInvestigationFileList}
        handleUploadFile={handleFileChange}
        uploadedFileColumns={uploadedFileColumns}
        stageStatus={stageStatus}
        nextStage={nextStage}
        setMoveToNext={setMoveToNext}
        handleGetData={handleGetData}
        handleModalOpen={handleModalOpen}
      />
      <FilePreviewModal
        isVisible={uploadFileModal}
        onClose={handleCancel}
        fileUrl={previewFile.url}
        fileName={previewFile.name}
        documentType={previewFile.documentType}
        handleUpload={handleUploadDocument}
        showUploadButton={previewFile.showUpload}
        key={selectedFile?.file?.uid || "no-file"}
        isLoading={preAuthClaim?.isLoading}
      />
      <RightSidePreviewModal
        key={documentUrl}
        documentUrl={documentUrl}
        handleCancel={handleCancelPreview}
        open={previewDocument}
      />
      <DocumentDeficiencyModal
        key={previewDocumentUrl}
        closeDeficiencyModal={closeDeficiencyModal}
        deficiencyModal={deficiencyModal}
        documentUrl={previewDocumentUrl}
        documentName={documentName}
        handleDownloadDocument={handleDownloadDocument}
        deficiencyQuestions={patients?.deficiencyCheckQuestions}
        selectedDocument={selectedDocument}
        setUploadedDocumentUrl={
          activeDocListType == "investigation"
            ? setUploadedInvestigationFileList
            : setUploadedImplantFileList
        }
      />

      {activeDocListType === "implant" ? (
        <UploadDocumentInPreAuthImplant
          // title="Confirm Document Upload"
          visible={isModalOpen}
          documentTypes={formattedDocumentTypeList?.[activeDocListType]}
          setDocumentTypes={setDocumentTypeList}
          // activeDocListType={activeDocListType}
          // onOk={handleModalOk}
          onCancel={handleModalClose}
          setDocumentUrls={setUploadedImplantFileList}
        />
      ) : (
        <UploadDocumentInPreAuth
          // title="Confirm Document Upload"
          visible={isModalOpen}
          documentTypes={formattedDocumentTypeList?.[activeDocListType]}
          setDocumentTypes={setDocumentTypeList}
          // activeDocListType={activeDocListType}
          // onOk={handleModalOk}
          onCancel={handleModalClose}
          setDocumentUrls={
            activeDocListType == "investigation"
              ? setUploadedInvestigationFileList
              : setUploadedImplantFileList
          }
        />
      )}
    </div>
  );
};

export default PreAuthAyushConatiner;
